import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import type { ScrollAnchor } from '@hm-spt/sola';

import * as api from '../api/client';
import { CatalogSidebar } from '../components/CatalogSidebar';
import { DataGrid } from '../components/DataGrid';
import { Pager } from '../components/Pager';
import { DetailDrawer } from '../components/DetailDrawer';
import type { SortOrder, TableMetaFromServer } from '../types';
import { detailOf } from '../lib/events';

interface CatalogViewData {
  pageTitle: string;
  pageSubtitle: string;
  searchQuery: string;
}

/**
 * 🎯 CatalogView — /catalog · /catalog/:tableName 라우트
 *
 * 기존 AdminApp 의 28테이블 뷰어 기능을 그대로 가짐. AppShell 의 outlet 안에
 * 마운트되어 페이지 단위로 동작.
 *
 * 라우터 프로토콜 구현:
 *   - captureScrollAnchor: 현재 테이블·검색어·정렬·오프셋 저장
 *   - ready: 초기 행 fetch 완료 Promise
 *   - restoreScrollAnchor: 저장된 상태 복원
 */
export class CatalogView extends SolaView<CatalogViewData> {
  private readonly sidebar = new CatalogSidebar();
  private readonly grid = new DataGrid();
  private readonly pager = new Pager();
  private readonly drawer = new DetailDrawer();

  // 라우트 파라미터 — 어떤 테이블을 처음 보여줄지
  private readonly initialTableName: string;

  // 서버 상태
  private tables: TableMetaFromServer[] = [];
  private tableByName = new Map<string, TableMetaFromServer>();
  private currentTable = '';
  private searchQuery = '';
  private sortCol = '';
  private sortOrder: SortOrder = 'asc';
  private limit = 50;
  private offset = 0;
  private total = 0;

  // 데이터 로드 완료 시그널 — ready() Promise 용
  private readyResolve: (() => void) | null = null;
  private readyPromise: Promise<void>;

  // 검색 디바운스
  private searchTimer?: ReturnType<typeof setTimeout>;

  override events = {
    'sort:change': { '*': 'handleSortChange' },
    'page:change': { '*': 'handlePageChange' },
    'row:open':    { '*': 'handleRowOpen' },
    input:         { '@search': 'handleSearchInput' },
    click:         { '@refresh': 'handleRefresh' },
  };

  constructor(config: { tableName?: string }) {
    super();
    this.initialTableName = config.tableName ?? '';
    this.data = {
      pageTitle: '카탈로그',
      pageSubtitle: '',
      searchQuery: '',
    };
    this.readyPromise = new Promise<void>((resolve) => {
      this.readyResolve = resolve;
    });
  }

  // ===== 라우터 프로토콜 =====

  override getTitle(): string {
    const tableLabel = this.tableByName.get(this.currentTable)?.label;
    return tableLabel
      ? `${tableLabel} — 카탈로그 — HCLTech`
      : '카탈로그 — HCLTech';
  }

  override async ready(): Promise<void> {
    return this.readyPromise;
  }

  override captureScrollAnchor(): ScrollAnchor {
    return {
      scrollY: window.scrollY,
      extra: {
        table: this.currentTable,
        search: this.searchQuery,
        sortCol: this.sortCol,
        sortOrder: this.sortOrder,
        offset: this.offset,
        limit: this.limit,
      },
    };
  }

  override restoreScrollAnchor(anchor: ScrollAnchor): void {
    // 같은 테이블로 돌아온 경우만 검색·정렬·페이지 복원
    if (
      anchor.extra?.table === this.currentTable &&
      typeof anchor.extra?.offset === 'number'
    ) {
      this.searchQuery = (anchor.extra.search as string) ?? '';
      this.sortCol = (anchor.extra.sortCol as string) ?? this.sortCol;
      this.sortOrder = (anchor.extra.sortOrder as SortOrder) ?? this.sortOrder;
      this.offset = anchor.extra.offset as number;
      this.limit = (anchor.extra.limit as number) ?? this.limit;
      this.data.searchQuery = this.searchQuery;
      this.notify('searchQuery');
      void this.loadRows();
    }
    if (anchor.scrollY != null) {
      window.scrollTo(0, anchor.scrollY);
    }
  }

  // ===== 라이프사이클 =====

  override onMount(): void {
    void this.loadTables();
  }

  override onUnmount(): void {
    super.onUnmount();
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-catalog">
        ${this.sidebar}
        <section class="catalog-main">
          <header class="catalog-topbar">
            <div class="catalog-topbar-left">
              <h1 class="page-title" data-bind="pageTitle">${this.data.pageTitle}</h1>
              <span class="page-subtitle" data-bind="pageSubtitle">${this.data.pageSubtitle}</span>
            </div>
            <div class="catalog-topbar-right">
              <div class="search-wrap">
                <input
                  class="search-input"
                  type="search"
                  placeholder="검색…"
                  autocomplete="off"
                  spellcheck="false"
                  data-action="search"
                  data-bind="searchQuery"
                  value="${this.data.searchQuery}"
                />
              </div>
              <button class="btn-ghost" data-action="refresh" title="새로고침">↻</button>
            </div>
          </header>
          ${this.grid}
          ${this.pager}
        </section>
        ${this.drawer}
      </div>
    `;
  }

  // ===== 데이터 로딩 =====

  private async loadTables(): Promise<void> {
    try {
      const data = await api.fetchTables();
      this.tables = data.tables;
      this.tableByName = new Map(this.tables.map((t) => [t.name, t]));
      this.sidebar.setTables(this.tables);
      this.sidebar.setApiStatus('ok', `API 연결됨 · ${this.tables.length}개 테이블`);

      // 초기 테이블 결정
      const targetName = this.initialTableName || this.tables[0]?.name;
      if (targetName && this.tableByName.has(targetName)) {
        this.activateTable(targetName);
      } else {
        // 파라미터 잘못된 경우 첫 테이블 선택
        if (this.tables[0]) this.activateTable(this.tables[0].name);
      }
    } catch (err) {
      this.sidebar.setApiStatus('err', 'API 연결 실패');
      this.grid.setError(`테이블 목록 로드 실패: ${(err as Error).message}`);
      this.readyResolve?.();
    }
  }

  private activateTable(name: string): void {
    const meta = this.tableByName.get(name);
    if (!meta) return;

    this.currentTable = name;
    this.sortCol = meta.defaultSort.col;
    this.sortOrder = meta.defaultSort.order;
    this.data.pageTitle = meta.label || name;
    this.data.pageSubtitle = name;
    this.notify('pageTitle');
    this.notify('pageSubtitle');

    this.sidebar.setCurrent(name);
    this.grid.setTable(meta, this.sortCol, this.sortOrder);
    this.drawer.close();
    void this.loadRows();
  }

  private async loadRows(): Promise<void> {
    if (!this.currentTable) return;
    this.grid.setError('');
    this.grid.setLoading(true);
    try {
      const result = await api.fetchRows({
        table: this.currentTable,
        search: this.searchQuery,
        sort: this.sortCol,
        order: this.sortOrder,
        limit: this.limit,
        offset: this.offset,
      });
      this.total = result.total;
      this.grid.setRows(result.rows);
      this.grid.setSort(result.sort || this.sortCol, result.order || this.sortOrder);
      this.pager.setState(this.total, this.limit, this.offset);
    } catch (err) {
      this.grid.setError(`데이터 로드 실패: ${(err as Error).message}`);
      this.grid.setRows([]);
      this.pager.setState(0, this.limit, 0);
    } finally {
      this.grid.setLoading(false);
      // 라우터에게 "데이터 준비 끝났음" 알림 (스크롤 복원용)
      this.readyResolve?.();
    }
  }

  // ===== 이벤트 핸들러 =====

  handleSortChange(e: Event): void {
    const detail = detailOf(e, 'sort:change');
    this.sortCol = detail.col;
    this.sortOrder = detail.order;
    this.offset = 0;
    void this.loadRows();
  }

  handlePageChange(e: Event): void {
    const detail = detailOf(e, 'page:change');
    this.limit = detail.limit;
    this.offset = detail.offset;
    void this.loadRows();
  }

  handleRowOpen(e: Event): void {
    const detail = detailOf(e, 'row:open');
    const meta = this.tableByName.get(this.currentTable);
    this.drawer.open(detail.row, meta?.label || this.currentTable);
  }

  handleSearchInput(_e: Event, target: HTMLElement): void {
    this.searchQuery = (target as HTMLInputElement).value;
    this.data.searchQuery = this.searchQuery;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.searchTimer = undefined;
      this.offset = 0;
      void this.loadRows();
    }, 280);
  }

  handleRefresh(): void {
    void this.loadRows();
  }
}
